import styled from "@emotion/styled";
import { Box, Checkbox, CircularProgress, Divider, FormControlLabel } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { EmailField, PasswordInput } from "../../Components/common";
import { CommonButton } from "../../Components/common/commonButton";
import LogoIcon from "../../assets/Images/New_logo copy.png";
import { fetchAzureAccessToken, loginUser } from "../../redux/slice/loginSlice";
import ROUTES from "../../utils/routes";
import AzureButton from "./AzureButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

// Styled components for new layout
const LoginContainer = styled("div")(() => ({
  display: "flex",
  width: "100%",
  height: "100vh",
  overflow: "hidden",
  position: "relative",
  fontFamily: "'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
}));

const LoginSection = styled("div")(() => ({
  width: "40%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#ffffff",
  padding: "50px 45px",
  position: "relative",
  boxShadow: "0 0 40px rgba(22, 114, 222, 0.15), -4px 0 20px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  backgroundImage: `
    radial-gradient(circle at 2px 2px, rgba(22, 114, 222, 0.03) 1px, transparent 0)
  `,
  backgroundSize: "40px 40px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-100px",
    right: "-100px",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
    animation: "float 6s ease-in-out infinite",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-150px",
    left: "-150px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.08) 0%, rgba(22, 114, 222, 0.03) 100%)",
    animation: "float 8s ease-in-out infinite reverse",
    zIndex: 0,
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) rotate(0deg)",
    },
    "50%": {
      transform: "translateY(-20px) rotate(5deg)",
    },
  },
}));

const ImageSection = styled("div")(() => ({
  width: "60%",
  height: "100vh",
  overflow: "visible",
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "50px 45px",
  paddingTop: "140px",
  backgroundColor: "#ffffff",
  backgroundImage: `
    radial-gradient(circle at 2px 2px, rgba(22, 114, 222, 0.03) 1px, transparent 0)
  `,
  backgroundSize: "40px 40px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-100px",
    right: "-100px",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
    animation: "float 6s ease-in-out infinite",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-150px",
    left: "-150px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.08) 0%, rgba(22, 114, 222, 0.03) 100%)",
    animation: "float 8s ease-in-out infinite reverse",
    zIndex: 0,
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) rotate(0deg)",
    },
    "50%": {
      transform: "translateY(-20px) rotate(5deg)",
    },
  },
  "& img": {
    width: "500px",
    height: "auto",
    maxWidth: "none",
    maxHeight: "none",
    objectFit: "contain",
    objectPosition: "center",
    transition: "transform 0.3s ease",
    position: "relative",
    zIndex: 2,
    filter: "drop-shadow(0 20px 60px rgba(22, 114, 222, 0.3)) drop-shadow(0 0 40px rgba(147, 197, 253, 0.4))",
    "&:hover": {
      transform: "scale(1.05)",
      filter: "drop-shadow(0 25px 70px rgba(22, 114, 222, 0.4)) drop-shadow(0 0 50px rgba(147, 197, 253, 0.5))",
    },
  },
}));

const LoginContent = styled("div")(() => ({
  width: "100%",
  maxWidth: "420px",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  position: "relative",
  zIndex: 2,
  animation: "fadeInUp 0.6s ease-out",
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const TitleText = styled("p")(() => ({
  fontSize: "34px",
  fontWeight: "700",
  color: "#1a1a1a",
  margin: "0 0 8px 0",
  fontFamily: "'Inter', 'Poppins', sans-serif",
  letterSpacing: "-0.5px",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: 0,
    width: "400px",
    height: "4px",
    background: "linear-gradient(90deg, #1672DE 0%, rgba(22, 114, 222, 0.3) 100%)",
    borderRadius: "2px",
  },
}));

const SubtitleText = styled("p")(() => ({
  fontSize: "16px",
  color: "#1672DE",
  margin: "16px 0 0 0",
  fontWeight: "500",
  letterSpacing: "0.2px",
  fontFamily: "'Inter', 'Poppins', sans-serif",
}));

const DividerContainer = styled(Divider)(() => ({
  margin: "20px 0",
  color: "#9e9e9e",
  position: "relative",
  "& .MuiDivider-wrapper": {
    textAlign: "center",
    padding: "0 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    position: "relative",
    zIndex: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: 0,
    transform: "translateY(-50%)",
    height: "4px",
    width: "90px",
    background: "linear-gradient(90deg, rgba(22, 114, 222, 0.3) 0%, #1672DE 100%)",
    borderRadius: "2px",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    right: 0,
    transform: "translateY(-50%)",
    height: "4px",
    width: "90px",
    background: "linear-gradient(90deg, #1672DE 0%, rgba(22, 114, 222, 0.3) 100%)",
    borderRadius: "2px",
    zIndex: 0,
  },
}));

// Styled wrapper for input fields
const StyledInputWrapper = styled("div")(() => ({
  width: "100%",
  marginBottom: "16px",
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Inter', 'Poppins', sans-serif",
    fontSize: "15px",
    borderRadius: "10px",
    backgroundColor: "#ffffff !important",
    border: "2px solid #e9ecef",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#1672DE",
      backgroundColor: "#ffffff !important",
      boxShadow: "0 2px 8px rgba(22, 114, 222, 0.1)",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiInputBase-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
    },
    "&.Mui-focused": {
      borderColor: "#1672DE",
      backgroundColor: "#ffffff !important",
      boxShadow: "0 4px 12px rgba(22, 114, 222, 0.15)",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiInputBase-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& .MuiOutlinedInput-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
    },
    "& fieldset": {
      border: "none !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-input": {
      backgroundColor: "#ffffff !important",
      color: "#1a1a1a !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "& input": {
      backgroundColor: "#ffffff !important",
      color: "#1a1a1a !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&::placeholder": {
      color: "#6c757d",
      opacity: 0.7,
    },
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
  "& input": {
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
  "& .MuiInputAdornment-root": {
    marginRight: "8px",
    "& img": {
      width: "20px",
      height: "20px",
      opacity: 0.6,
    },
  },
}));

const RememberForgotContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "12px",
  marginTop: "8px",
  padding: "8px 0",
}));

const ForgotPasswordButton = styled("button")(() => ({
  background: "none",
  border: "none",
  color: "#1672DE",
  fontSize: "14px",
  fontWeight: "600",
  cursor: "pointer",
  padding: "6px 12px",
  borderRadius: "6px",
  fontFamily: "'Inter', 'Poppins', sans-serif",
  transition: "all 0.2s ease",
  position: "relative",
  "&:hover": {
    color: "#1565c0",
    backgroundColor: "rgba(22, 114, 222, 0.08)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

// Decorative elements
const DecorativeCircle = styled("div")(({ top, right, left, bottom, size, delay }) => ({
  position: "absolute",
  top: top || "auto",
  right: right || "auto",
  left: left || "auto",
  bottom: bottom || "auto",
  width: size || "120px",
  height: size || "120px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
  border: "2px solid rgba(22, 114, 222, 0.1)",
  animation: `pulse 3s ease-in-out infinite ${delay || "0s"}`,
  zIndex: 1,
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      opacity: 0.6,
    },
    "50%": {
      transform: "scale(1.1)",
      opacity: 0.8,
    },
  },
}));

const DecorativeLine = styled("div")(({ top, right, left, bottom, width, height, rotation, delay }) => ({
  position: "absolute",
  top: top || "auto",
  right: right || "auto",
  left: left || "auto",
  bottom: bottom || "auto",
  width: width || "200px",
  height: height || "2px",
  background: "linear-gradient(90deg, transparent, rgba(22, 114, 222, 0.3), transparent)",
  transform: `rotate(${rotation || 0}deg)`,
  animation: `slideIn 2s ease-out ${delay || "0s"} both`,
  zIndex: 1,
  "@keyframes slideIn": {
    "0%": {
      opacity: 0,
      transform: `rotate(${rotation || 0}deg) translateX(-50px)`,
    },
    "100%": {
      opacity: 1,
      transform: `rotate(${rotation || 0}deg) translateX(0)`,
    },
  },
}));

const FooterText = styled("p")(() => ({
  fontSize: "12px",
  color: "#757575",
  textAlign: "center",
  margin: "12px 0 0 0",
  lineHeight: "1.5",
  fontFamily: "'Inter', 'Poppins', sans-serif",
  "& a": {
    color: "#1672DE",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& button": {
    background: "none",
    border: "none",
    color: "#1672DE",
    textDecoration: "none",
    cursor: "pointer",
    padding: 0,
    fontSize: "12px",
    fontFamily: "'Inter', 'Poppins', sans-serif",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const Login = () => {
  const navigate = useNavigate();
  const { isLoggedIn, isLoading } = useSelector((state) => state?.login);
  const [openDialog, setOpenDialog] = useState(false);

  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved email from localStorage on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    setFormData({ ...formData, [key]: value });
  };

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    
    // If unchecking, remove saved email
    if (!checked) {
      localStorage.removeItem("rememberedEmail");
    }
  };

  const handleCloseDialogs = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authorization_code = params.get("code");
    if (authorization_code?.length > 0) {
      dispatch(fetchAzureAccessToken(authorization_code));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate(ROUTES.MUSU);
    }
  }, [isLoggedIn, navigate]);

  const handleLoginClick = async () => {
    const resultAction = await dispatch(loginUser(formData));
    
    if (loginUser.fulfilled.match(resultAction)) {
      // Save email to localStorage if remember me is checked
      if (rememberMe && formData.email) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    } else if (loginUser.rejected.match(resultAction)) {
      const code = resultAction.payload?.code;
      if (code === 'password_update_warning') {
        navigate(ROUTES.FORGETPASSWORD, { state: { type: "update_password" } });
      }
    }
  };


  return (
    <LoginContainer>
      <LoginSection>
        {/* Decorative elements */}
        <DecorativeCircle top="10%" right="10%" size="80px" delay="0s" />
        <DecorativeCircle top="70%" left="5%" size="100px" delay="1s" />
        <DecorativeLine top="20%" right="15%" width="150px" rotation="45deg" delay="0.5s" />
        <DecorativeLine bottom="25%" left="10%" width="180px" rotation="-30deg" delay="1s" />
        
            {isLoading && (
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
              background: "rgba(255, 255, 255, 0.95)",
              zIndex: 1000,
              backdropFilter: "blur(4px)",
                }}>
                <CircularProgress
                  style={{
                color: "#1672DE",
                  }}
                />
              </div>
            )}
        <LoginContent>
          <div>
            <TitleText>Let's Get Started</TitleText>
            <SubtitleText>Log in to your account</SubtitleText>
          </div>
          
          <div style={{ width: "100%" }}>
            <AzureButton />
        </div>
          
          <DividerContainer>Or with email and password</DividerContainer>
          
          <ValidatorForm
            onError={() => null}
            style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
            <StyledInputWrapper>
              <EmailField
                value={formData.email}
                placeholder="Enter your email"
                handleChange={handleChange}
                name="email"
                width="100%"
              />
            </StyledInputWrapper>
            
            <StyledInputWrapper>
              <PasswordInput
                value={formData.password}
                handleChange={handleChange}
                name="password"
                isExistingPassword={true}
                width="100%"
                placeholder="Enter your password"
              />
            </StyledInputWrapper>

            <RememberForgotContainer>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    size="small" 
                    sx={{ 
                      color: "#1672DE", 
                      "&.Mui-checked": { color: "#1672DE" },
                      "& .MuiSvgIcon-root": {
                        fontSize: "18px",
                      }
                    }} 
                  />
                }
                label={
                  <span style={{ 
                    fontSize: "14px", 
                    color: "#424242",
                    fontFamily: "'Inter', 'Poppins', sans-serif",
                    fontWeight: "500"
                  }}>
                    Remember me
                  </span>
                }
              />
              <ForgotPasswordButton
                type="button"
                onClick={() => navigate(ROUTES.FORGETPASSWORD, { state: { type: "forgot_password" } })}>
                Forgot Password?
              </ForgotPasswordButton>
            </RememberForgotContainer>

            <CommonButton
              text="Login"
              handleClick={handleLoginClick}
              disabled={!formData.email.trim()}
              style={{
                width: "100%",
                backgroundColor: "#1672DE",
                color: "#ffffff",
                padding: "12px 14px",
                fontSize: "16px",
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "10px",
                boxShadow: "0 4px 16px rgba(22, 114, 222, 0.3)",
                transition: "all 0.3s ease",
                fontFamily: "'Inter', 'Poppins', sans-serif",
                marginTop: "4px",
                minHeight: "48px",
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "#1565c0",
                  boxShadow: "0 6px 24px rgba(22, 114, 222, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                  boxShadow: "none",
                },
              }}
            />
            
            <FooterText>
              Check Epigee <button type="button" onClick={() => setOpenDialog(true)}>privacy policy</button> and <a href="mailto:abhishek.sharma@epigeeus.com">contact</a> for details.
            </FooterText>
          </ValidatorForm>
        </LoginContent>
      </LoginSection>
      
      <ImageSection>
        {/* Decorative elements */}
        <DecorativeCircle top="15%" right="8%" size="90px" delay="0.5s" />
        <DecorativeCircle top="75%" left="8%" size="110px" delay="1.5s" />
        <DecorativeLine top="30%" right="12%" width="160px" rotation="35deg" delay="0.8s" />
        <DecorativeLine bottom="30%" left="12%" width="200px" rotation="-25deg" delay="1.2s" />
        <div style={{ position: "relative", display: "inline-block" }}>
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "120%",
            height: "120%",
            background: "radial-gradient(circle, rgba(147, 197, 253, 0.5) 0%, rgba(22, 114, 222, 0.3) 50%, transparent 70%)",
            filter: "blur(50px)",
            zIndex: 1,
          }} />
          <img
            src={LogoIcon}
            alt="InsurPremMgmt Logo"
            style={{ position: "relative", zIndex: 2 }}
          />
        </div>
      </ImageSection>
      <Dialog open={openDialog} onClose={handleCloseDialogs} maxWidth={"lg"}>
        <DialogTitle><u><h2>Privacy Notice</h2></u></DialogTitle>
        <DialogContent sx={{ overflowY: "scroll" }}>
          <div>
            {/* <h1 class="wp-block-heading">Privacy Notice</h1> */}
            <p>
              This Privacy Notice is issued on behalf of the Epigee Insurance group of companies. When there is mention of "Epigee," "we," "us," or "our" in this notice, we are referring to the relevant company within the Epigee group responsible for processing the information.
            </p>
            <p>
              Epigee believes strongly in protecting the confidentiality and privacy of information that you provide to us that relates to an identifiable individual ("personal information" or "personal data") and which we collect, use, share, disclose, and retain. This notice is intended to inform you about your choices regarding the use, access, and correction of your personal information. We are committed to ensuring any personal data we receive is protected and handled in accordance with applicable data-protection laws.
            </p>
            <p>
              Insurance involves the use and disclosure of your personal data by various insurance market participants such as intermediaries, brokers, insurers, and reinsurers. There could be instances, such as when you request information, when we may obtain personal identifiable information from you or about you. Such personal data may include your name, mailing address, email, telephone number, or business contact, etc. This information will not be collected without your knowledge.
            </p>
            <p>
              Please read the following carefully to understand how we will treat your personal information.
            </p>
            <h3>Collecting your data</h3>
            <p>
              We collect and use relevant information to provide the insurance coverage that benefits you and to meet our legal obligations.
            </p>
            <p>
              We may collect a range of personal and business information supplied by you or third parties on your behalf.
            </p>
            <p>
              Specifically, we may collect the following personal information throughout the duration of your relationship with us:
            </p>
            <ul>
              <li>
                Basic personal details such as your name, address, date of birth or age, gender, marital status, and additional information about your insurance requirements
              </li>
              <li>
                Business contact details (such as email and telephone details) related to correspondents, brokers and/or other relevant connections to insurance business
              </li>
              <li>
                Personal information related to your insurance requirements and details of any specific claims
              </li>
            </ul>
            <p>
              We may also collect special-category data, such as race, ethnic origin, political opinions, religion, trade-union membership, genetics, biometrics, health, sex life or sexual orientation when needed to provide insurance or process claims. This information will only be used for the specific purpose for which it was provided and to carry out the agreed service. In certain instances, we may also need to collect and process special-category data relating to individuals who may benefit from the policy.
            </p>
            <p>
              Where necessary, we will obtain your consent to use special-category data and may do this via an intermediary or broker.
            </p>
            <p>
              We may collect, use, or disclose to third parties special categories of personal information about other individuals, such as employees, family, or members of your household. Before providing us with personal information about other individuals, you agree: (a) to notify the individual about the content of this Privacy Notice; (b) inform the individual how and why their information will be used and, (c) if requested by us, obtain their permission to share their personal information with us by requiring the individual to sign a consent form.
            </p>
            <h3>Collection of Information from Children</h3>
            <p>
              Our services are not directed to nor intended for children under the age of 13 and we do not knowingly collect personal information from children under the age of 13.
            </p>
            <h3>Other Considerations</h3>
            <p>
              When you use Epigee products, services, and applications, or post information on an Epigee channel or use social networking services such as Facebook, LinkedIn, X, Instagram, etc, the personal information and content you share are visible to other users and can be read, collected, or used by them.
            </p>
            <h3>Using Your Information</h3>
            <p>
              We will only use your personal data when the law allows us to do so. Generally, we will use your personal data in the following circumstances:
            </p>
            <ul>
              <li>Assessing your application for a product, service or quote</li>
              <li>Providing and administrating relevant insurance policies</li>
              <ul>
                <li>Client care, including communicating with you</li>
                <li>Payments to and from individuals</li>
              </ul>
              <li>Verifying your identity and carrying out sanctions/anti-fraud/financial crime checks</li>
              <li>Handling claims</li>
              <ul>
                <li>Managing insurance and reinsurance claims</li>
                <li>Defending or prosecuting legal claims</li>
                <li>Investigation or prosecuting fraud</li>
              </ul>
              <li>Dealing with complaints</li>
              <li>General risk modelling</li>
              <li>Renewals</li>
              <ul>
                <li>Contacting the policyholder to renew your policy</li>
                <li>DeEvaluating the risk to be covered and matching to appropriate policy/premium</li>
                <li>Payment of premium</li>
              </ul>
              <li>Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests</li>
              <li>Where we need to comply with legal or regulatory obligations</li>
              <li>Transferring books of business, company sales and reorganisations</li>
            </ul>
            <h3>Retention of Your Information</h3>
            <p>Retention periods for personal information vary. We retain personal data only for as long as it is necessary and for the purpose for which it was originally collected. We consider the following obligations when setting retention periods for personal information and the records we maintain: the need to retain information to accomplish the business purposes or contractual obligations for which it was collected; our duties to effectuate our clients’ instructions with respect to personal information we process on their behalf; our duties to comply with mandatory legal and regulatory record-keeping requirements; and to fulfil statutory and regulatory requirements, and other legal impacts such as applicable statute of limitations periods. We may also retain personal information for other purposes delineated in applicable privacy laws.</p>
            <p>We will securely delete or erase your personal information if there is no valid business reason for retaining your data.</p>
            <h3>Storing Your Information</h3>
            <p>Given the nature of insurance, your information may be shared with, and used by, a number of third parties in the insurance sector—for example, insurers, agents or brokers, reinsurers, loss adjusters, premium collection, claims-validation processors and providers, sub-contractors, regulators, law enforcement agencies, fraud and crime-prevention detection agencies, as well as compulsory insurance databases. We will only disclose your personal information in connection with the insurance cover we provide and to the extent required or permitted by law.</p>
            <p>We require all third parties to respect the security of your personal data. Parties processing data on our behalf are only permitted to process your personal information for specified purposes and in accordance with our instructions.</p>
            <h3>Transfer of your Information</h3>
            <p>From time to time, we may need to share your personal information with other insurance market participants or their affiliates, who may be based outside of your country of residence. Furthermore, we may also make other disclosures of your personal information, for example, if we receive a legal or regulatory request from a foreign law enforcement entity.</p>
            <p>We will always take steps to ensure any international transfer of information is carefully managed to protect your rights and interests:</p>
            <ul>
              <li>We will only transfer your personal information to countries which are recognized as providing an adequate level of legal protection or where we can be satisfied alternative arrangements are in place to protect your privacy rights</li>
              <li>Where applicable, transfers of data overseas will be covered by standard contractual clauses adopted by the European Commission, which give specific contractual protections designed to ensure your personal information receives an adequate and consistent level of protection</li>
              <li>Any requests for information we receive from law enforcement or regulators will be carefully checked before personal information is disclosed</li>
            </ul>
            <p>Please contact us if you want further information on the specific mechanism used by us when transferring your personal data.</p>
            <p>We apply appropriate safeguards to prevent your personal data being lost, used, altered, disclosed, or accessed in an unauthorised way. In addition, we limit access to personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions, and they are subject to a duty of confidentiality.</p>
            <p>We have put in place procedures to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where we are legally required to do so.</p>
            <h3>Confidentiality and Security</h3>
            <p>If personally identifiable information (i.e. name, address, email, telephone details) is provided to any third parties, we will require they maintain such information in strictest confidence in compliance with our Privacy Policy.</p>
            <p>We take the security of your personal information seriously and make appropriate technical and organizational measures against unauthorized or unlawful processing of personal data, and against accidental loss, destruction of, or damage to, personal data.</p>
            <p>The security of your personal information is important to us. Epigee seeks to use reasonable administrative, technical, and physical safeguards to protect personal information within the organization. However, no method of data transmission or storage system can be guaranteed to be 100% secure.</p>
            <h3>Your Rights</h3>
            <p>Under certain circumstances, you have rights under data protection laws in relation to your personal data:</p>
            <ul>
              <li>Request access to your personal data</li>
              <li>Request correction of your personal data</li>
              <li>Request erasure of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>DeRequest restriction of processing your personal data</li>
              <li>Request transfer of your personal data</li>
              <li>Right to withdraw consent of your personal data</li>
            </ul>
            <p>Request access to your personal data (commonly known as a “data subject access request”). You are entitled to a confirmation whether we are processing your data; a copy of your data and information about purposes of processing; identity of those we disclose it to; whether we transfer it abroad and how we protect it; how long we retain data; what rights you have; how data was acquired; and how you can make a complaint. This enables you to receive a copy of the personal data we hold about you and to check we are lawfully processing it.</p>
            <p>Request correction of your personal data. This enables you to have any incomplete, inaccurate, or outdated information to be corrected.</p>
            <p>Request erasure of your personal data. Sometimes referred to as “the right to be forgotten,” this right entitles you to request your personal information to be deleted or removed from our systems and records. However, this right applies in certain circumstances. Examples of when this right applies to the personal data we hold (subject to exemptions) include: when we no longer need the personal information for the purpose it was collected; if you withdraw consent to our use of your information and no legal justification supports our continued use of your personal data; if you object to the way in which we use your information and we have no grounds to continue using it; if we have used your personal data unlawfully; and, if the personal information needs to be erased to comply with the law.</p>
            <p>Please be advised there may be consequences if you exercise your right to erasure. If you subsequently make a claim, it may be impossible to administer your claim without your personal data.</p>
            <p>Object to processing of your personal data. You have the right to object to our use of your personal information in certain circumstances. You can object to our use of your personal information where you have grounds relating to a particular situation and the legal justification that we rely on for using your personal information in our, or a third-party’s, legitimate interest. However, despite your objections, we may continue to use your personal information where there are legitimate grounds to do so, or we need to use your personal data in connection with legal claims.</p>
            <p>Request restriction of processing your personal data. You have the right to request that we restrict or suspend the use of your personal information. However, this right only applies in certain circumstances. For example, you can exercise this right if: you think the personal information we hold about you is inaccurate; the processing is unlawful and you oppose the erasure of your personal information and instead request the restriction of its use; we no longer need the personal information for the purposes we have used it; or you feel it impacts your fundamental rights and freedoms. You also have the right to object where we are processing your personal data for direct marketing purposes. In some cases, we may demonstrate we have legitimate grounds to process your information which may override your objections.</p>
            <p>Request the transfer of your personal data. You have the right under certain circumstances to data portability, which requires us to provide personal information to you or a third party in a commonly used, machine readable format, but only where the processing of that information is based upon your consent or a contract to which you are a party.</p>
            <p>Right to withdraw consent of your personal data. Where we have relied upon your consent to process personal information, you have the right to withdraw that consent. This right only applies where we process personal information based upon your written consent.</p>
            <h3>Ability to Opt Out</h3>
            <p>When you engage us to provide insurance services, we may ask for personal information which we maintain in encrypted form on secure servers. Epigee does not sell personal information; however, if we propose to use your personal data for any purposes, other than those described in this notice and/or in any other specific notices, you may "opt out" to having your information shared, by contacting us at <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <p>We will not collect or use sensitive personal information for purposes other than those described in this notice unless we have obtained your permission.</p>
            <h3>State-Specific Privacy Laws</h3>
            <p>Many states have developed their own privacy laws that apply to individuals and businesses that live and do business in such states. Individual state laws vary; therefore, we encourage you to review your rights as they pertain to state specific consumer privacy rights. For your convenience, below is a summary of current state privacy protections which have been enacted.</p>
            <h3>California Privacy Rights (CCPA)</h3>
            <p>California Civil Code Section §1798.83 and the California Consumer Privacy Act (CCPA) permits users of our website who are California residents to request certain data regarding our disclosure of personal information to third parties for their direct marketing purposes. The CCPA also provides California residents the right ‘To Be Forgotten’ by a company.</p>
            <p>To make such a request, please send us a message via email: <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Right to Know</h3>
            <p>A California resident has the right to request that we disclose what Personal Information we collect, use, disclose or sell. You may request that we disclose the following information upon receipt of a verifiable consumer request:</p>
            <ol>
              <li>The categories of Personal Information collected and categories of sources from which the Personal Information is collected</li>
              <li>The business or commercial purpose for collecting or selling Personal Information</li>
              <li>The categories of third parties with whom we share Personal Information</li>
              <li>The specific pieces of Personal Information we have collected about you</li>
            </ol>
            <h3>Right to Delete</h3>
            <p>As a California resident, you have the right to request that we delete any Personal Information about you which we have previously collected. If it is necessary for us to maintain the Personal Information for certain purposes, we are not required to comply with your deletion request. If we determine that we will not delete your Personal Information when you request us to do so, we will inform you and tell you why we are not deleting it.</p>
            <h3>Right to Opt Out of Sale of Personal Information</h3>
            <p>We do not sell Personal Information, including the Personal Information of minors under the age of 16. However, pursuant to applicable law, a California resident may request that their information not be sold in the future. To do so, please email: <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>No Discrimination</h3>
            <p>You have the right not to be discriminated against because you exercised any of your rights under the CCPA. If you would like to exercise any such rights, please email: <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Colorado Privacy Act (CPA)</h3>
            <p>The Colorado Privacy Act (CPA) provides consumers with certain rights related to their personal data.</p>
            <p>The CPA provides five main rights for the consumer:</p>
            <ol>
              <li>Right of access: you have the right to confirm whether a controller is processing your personal data and to have the sole right to access your personal data</li>
              <li>Right to correction: you have the right to correct inaccuracies in any personal data, taking into account the nature of the personal data and the purposes of the processing of your personal data</li>
              <li>Right to delete: you have the right to delete personal data concerning the consumer</li>
              <li>Right to data portability: you have the right to obtain your personal data in a portable and, to the extent technically feasible, readily usable format that allows you the consumer to transmit the data to another entity without hindrance</li>
              <li>Right to opt out: you have the right to opt out of the processing of your personal data purposes of:</li>
              <ul>
                <li>targeted advertising</li>
                <li>the sale of personal data</li>
                <li>profiling in furtherance of decisions that produce legal or similarly significant effects on you as the consumer</li>
              </ul>
              <li>Right to appeal: the CPA also provides you the right to appeal a business’ denial to take action within a reasonable time period. A business must respond to a request within 45 days of receipt and may subsequently extend that deadline by an additional 45 days when reasonably necessary. When a business elects to extend that deadline, it must notify you within the initial 45-day response period.</li>
            </ol>
            <p>If you are a Colorado resident and would like to exercise any such rights, please send a request to <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Connecticut Data Privacy Act (CTDPA)</h3>
            <p>The Connecticut Data Privacy Act (CTDPA) provides consumers with certain rights related to their personal data. Under the Act, these rights:</p>
            <ol>
              <li>Allow consumers to opt-out of the processing of sensitive personal information</li>
              <li>Collect and process only the minimum amount of data needed for the processing purpose</li>
              <li>Provide consumers with a privacy notice</li>
              <li>Conduct data protection assessments where the processing may pose a risk</li>
            </ol>
            <p>The CTDPA applies to Connecticut businesses as well as non-Connecticut businesses that interact with Connecticut consumers.</p>
            <p>If you are a Connecticut resident and would like to exercise any such rights, please send a request to <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Montana Consumer Data Privacy Act (MTCDPA)</h3>
            <p>The Montana Consumer Data Privacy Act (MTCDPA) will go into effect October 1, 2024, and imposes transparency and disclosure obligations on a controller (i.e., an individual, or legal entity that, alone or jointly with others, determines the purpose and means of processing personal data) who either conducts business in Montana or produces products or services that are targeted to Montana residents; and either (a) controls or processes personal data of at least 50,000 Montana consumers or (b) controls or processes personal data of at least 25,000 Montana consumers and derives over 25% of gross revenue from the sale of any personal data.</p>
            <p>The MTCDPA grants Montana residents acting in an individual context, and not in a commercial or employment context (consumers), certain access and control rights concerning their personal data. A consumer may submit a request to a controller to:</p>
            <ul>
              <li>Confirm whether the controller is processing the consumer’s data and provide access to the consumer’s data</li>
              <li>Correct inaccurate personal data of the consumer</li>
              <li>Delete personal data about the consumer</li>
              <li>Obtain a copy of the consumer’s personal data (i.e., data portability)</li>
              <li>Opt out of the processing of the consumer’s personal data for purposes of targeted advertising, the sale of personal data, or profiling in furtherance of solely automated decisions that produce legal or similarly significant effects concerning the consumer.</li>
            </ul>
            <p>A controller must respond to consumer requests to exercise their rights granted by the statute within 45-days, though that time period may be extended for an additional 45 days when reasonably necessary considering the complexity and number of the consumer’s requests. In addition, MTCDPA also grants consumers the right to appeal the controller’s refusal to take action on requests to exercise their rights. Furthermore, a controller must respond to an appeal, in writing, within 60-days. If the appeal is denied, the controller must provide the consumer with a method for contacting the Montana Attorney General.</p>
            <p>If you are a Montana resident and would like to exercise any rights noted above, please send a request to <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Oregon Consumer Data Privacy Act (OCDPA)</h3>
            <p>The Oregon Consumer Data Privacy Act (OCDPA) took effect July 1, 2024 and applies to a company that either conducts business in Oregon or provides products or services to consumers in Oregon and meets one or two thresholds (i) control or process the personal data of a 100,000 or more consumers or (ii) control or process the personal data of 20,000 or more consumers and derive 25% or more of its annual gross revenue from selling personal data.</p>
            <p>The OCDPA, like certain other State Data Privacy Laws, contains the regulatory framework of the European Union’s GDPR, which distinguishes roles and responsibilities between controllers and processors. The OCDPA defines a controller as a person that alone or jointly with others, determines the purpose and means of processing personal data, and a “processor” as an entity that processes personal data on behalf of a controller.</p>
            <p>Controllers may only process personal data that is “adequate, relevant and reasonably necessary” for specific purposes. Controllers also are required to implement safeguards to protect the confidentiality, integrity and accessibility of personal data and to reduce reasonably foreseeable risks of harm to consumers.</p>
            <p>The OCDPA provides a variety of individual consumer rights that align with most other State Privacy Data Laws. These rights provide consumers with a right to access, correct, delete, and obtain a copy of their personal data, and to opt-out of the selling of personal data and/or sharing of personal data for targeted advertising. In addition, included are consumer rights to data portability and to obtain a list of third parties to which their personal data was disclosed.</p>
            <p>Under the OCDPA, a controller must respond to a consumer’s request to exercise a right withing 45-days of receipt of such request. Like most other State Data Privacy Laws, if the controller denies a consumer’s request, the controller must explain the justification for the denial and include instructions for appeal that are conspicuously available and similar to the process for submitting consumer rights requests.</p>
            <p>If you are an Oregon resident and would like to exercise any rights noted above, please send a request to <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Texas Data Privacy and Security Act (TDPSA)</h3>
            <p>The state of Texas enacted a comprehensive consumer privacy law by passing the Texas Data Privacy and Security Act (TDPSA). The law took effect July 1, 2024. The TDPSA contains many provisions that are similar to other state privacy laws, however, the TDPSA applies to a broader range of individuals and businesses (known as “controllers” under the statute) regardless of revenues or the number of individuals whose personal data is processed or sold. In addition, the TDPSA requires all controllers to recognize universal opt-outs (e.g., web browser privacy settings).</p>
            <p>The consumer rights granted in the TDPSA are consistent with other state privacy laws and include:</p>
            <ul>
              <li>The right to know whether a controller is processing the consumer’s personal data.</li>
              <li>The right to receive a portable copy, in digital format, of the consumer’s personal data processed by the controller.</li>
              <li>The right to request deletion of personal data provided by or obtained by or obtained about the consumer.</li>
              <li>The right to request a correction of inaccurate personal data.</li>
              <li>The right to opt out of sales of personal data, targeted advertising, and profiling in furtherance of a decision that produces a legal or similarly significant effect concerning the consumer.</li>
              <li>The right to appeal any refusal to take action on any of the above requests.</li>
            </ul>
            <p>Furthermore, the TDPSA requires controllers to practice data minimization (only using personal data as reasonably necessary), maintain privacy policies and include a notice whether the business sells sensitive personal data. Epigee Insurance Group of companies will never sell sensitive personal data.</p>
            <p>If you are a Texas resident and would like to exercise any rights noted above, please send a request to <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Utah Consumer Privacy Act (UCPA)</h3>
            <p>The Utah Consumer Privacy Act (UCPA) which took effect December 31, 2023, protects consumers against excessive data processing practices and gives consumers more control over their personal data. UCPA grants consumers rights. The Act imposes obligations on “controllers” and “processors.”</p>
            <p>The law describes controllers as an entity that determines the purposes for which and means by which personal is processed. Processors are entities that process personal data on behalf of a controller. According to the Act, processors must follow many, if not all, of the same regulations governing controllers.</p>
            <p>Under the UCPA framework, Utah residents are granted the follow six categories of rights:</p>
            <ul>
              <li>Right to know: the right to verify if a data controller is processing their personal data</li>
              <li>Right of access: the right to access their personal data</li>
              <li>Right to deletion: limited right to request their personal data be deleted by the controller</li>
              <li>Right of copy: the right to obtain a copy of their personal data in a format that’s both portable and legible</li>
              <li>Right to opt out: the right to opt out of the processing of the consumer’s personal data for the purposes of targeted advertising or the sale of their personal data</li>
              <li>Right to avoid discrimination: the right to avoid discrimination for exercising their UCPA rights</li>
            </ul>
            <p>In addition, UCPA requires businesses to be transparent about data practices and requires the controller to provide Utah consumers with a reasonably accessible and clear privacy notice which describes how consumers personal data is collected, used and shared.</p>
            <p>If you are a Utah resident and would like to exercise any such rights, please send a request to: <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <h3>Virginia Consumer Data Protection Act (VCDPA)</h3>
            <p>The Virginia Consumer Data Protection Act (VCDPA) provides consumers with certain rights related to their personal data. Under the Act, these rights include:</p>
            <ol>
              <li>The right to know, access, and confirm personal data</li>
              <li>The right to delete personal data</li>
              <li>The right to correct inaccuracies in personal data</li>
              <li>The right to data portability (i.e., easy, portable access to all pieces of personal data held by a company)</li>
              <li>The right to opt out of the processing of personal data for targeted advertising purposes</li>
              <li>The right to opt out of the sale of personal data</li>
              <li>The right to opt out of profiling based upon personal data</li>
              <li>The right to not be discriminated against for exercising any of the preceding rights</li>
            </ol>
            <p>If you are a Virginia resident and would like to exercise any such rights, please send a request to: <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <p>In addition to the privacy laws enacted by the various states noted above, more states have passed laws that will go into effect over the next two years. As such, beginning January 2025, five states’ consumer privacy laws will take effect to protect individuals from privacy violations. These laws create standards for how businesses collect, use and store consumer data.</p>
            <p>Delaware, Iowa, Nebraska and New Hampshire privacy laws take effect January 1, 2025, while New Jersey’s are effective January 15, 2025. The applicability of consumer privacy laws is similar and takes into account the following key points:</p>
            <p>Applicability:</p>
            <ul>
              <li>Applies to companies that conduct business in the respective state, produce products or services targeted to the state’s residents, and have control or process personal data</li>
              <li>The state consumer privacy laws include specific thresholds, such as minimum revenue amounts, data-processing volume and/or the number of residents affected</li>
            </ul>
            <p>Consumer protection: what rights do state privacy laws give to consumers?</p>
            <ul>
              <li>Right to access: to confirm whether a business (the controller) is processing personal data and access such data</li>
              <li>Right to correct: the right to correct inaccuracies in the consumer’s personal data</li>
              <li>Right to delete: the right to delete personal data concerning the consumer</li>
              <li>Right to data portability: the right to obtain personal data in a portable and, if feasible, readily usable format that allows the consumer to transmit data to another entity without hindrance, where processing is carried out by automated means</li>
              <li>Right to obtain: the right to obtain categories of third parties to whom the consumer’s personal data was disclosed</li>
              <li>Right to opt out: the right to opt out of the process of personal data for purposes of (i) targeted advertising, (ii) sale of personal data, (iii) profiling decisions that significantly affect the consumer</li>
            </ul>
            <p>The consumer privacy laws also govern businesses that control (controller) or process (processor). Such businesses are required to adhere to specific protections of consumers’ sensitive data, implement and maintain reasonable administrative and data security appropriate to the nature of the personal data to protect confidentiality, integrity, and accessibility of personal data.</p>
            <p>In addition, businesses are required to clearly disclose to consumers if they sell personal data to third parties or process personal data for targeted advertising, and provide a clear method for consumers to opt out. Notably, similar to the California Consumer Privacy Act, sale is broadly defined as the exchange of personal data for monetary or other valuable consideration by the controller to a third party.</p>
            <p>NOTE: The Epigee group of companies do not sell clients information or personal data.</p>
            <h3>Changes to our Privacy Notice</h3>
            <p>We review this Privacy Notice regularly and reserve the right to make changes at any time to comply with legal requirements. We encourage you to review this page for any updates to the Privacy Notice. If there will be any significant changes made to the use of your personal information in a manner that is different from that stated at the time of collection, we will notify you by posting a notice on our website.</p>
            <h3>How to contact us</h3>
            <p>For questions or concerns relating to our Privacy Notice or data protection practices, or if you would like to exercise any of your rights as defined above, please email us at <a href="mailto:compliance@epigee.com" style={{ color: "blue", cursor: "pointer" }}>compliance@epigee.com</a></p>
            <p>You may also contact our principal firm and Managing Agent, Asta Managing Agency at:</p>
            <p>Data Protection Manager</p>
            <p>5th Floor Camomile Court</p>
            <p>23 Camomile Court</p>
            <p>London EC3A 7LL</p>
            <p>UK</p>
            <p><a href="mailto:DPM@asta-uk.com" style={{ color: "blue", cursor: "pointer" }}>DPM@asta-uk.com</a></p>
            <h3>Complaints</h3>
            <p>If you are not satisfied with our response or believe we are not processing your personal data in accordance with legal requirements, you can make a complaint to the relevant Data Protection Authority.</p>
          </div>
        </DialogContent>
        <DialogActions>
          <CommonButton
            text="Close"
            handleClick={handleCloseDialogs}
            sx={{
              backgroundColor: "#ffff",
              color: "#000000",
              border: "1px solid #BCBDBF",
            }}
          />
        </DialogActions>
      </Dialog>
    </LoginContainer>
  );
};

export default Login;
